/**
 * End Consumer Dashboard — Home page for the END_CONSUMER persona.
 *
 * The end-consumer persona doesn't have loans / portfolio exposure, so we
 * adapt Screen 1 of the Borrower Portal v3 design pass to a lighter surface:
 *
 *   1. Greeting header — `GreetingHeader` (same component as borrower, with
 *      end-consumer stats: active applications + pending actions)
 *   2. Astor briefing hero — same component as LoanApplicantDashboard
 *   3. KPI strip — active applications / pending actions / documents
 *   4. Active applications panel — reuses `ActiveLoansPanel` (application
 *      stage progress reads the same way for borrowers and end-consumers)
 *   5. Insights strip — reuses `PortfolioInsightsStrip`
 */

import AssignmentIcon from '@mui/icons-material/Assignment';
import FolderIcon from '@mui/icons-material/Folder';
import HomeIcon from '@mui/icons-material/Home';
import PendingIcon from '@mui/icons-material/PendingActions';
import { GlassAlert, Grid, iconSizes, KpiTile, Stack, useI18n } from '@ops-matrix/design-system-ui';
import { useMemo } from 'react';
import { ActiveLoansPanel } from '../../components/dashboard/ActiveLoansPanel';
import { AstorBriefing } from '../../components/dashboard/AstorBriefing';
import { GreetingHeader, type GreetingHeaderStat } from '../../components/dashboard/GreetingHeader';
import { PortfolioInsightsStrip } from '../../components/dashboard/PortfolioInsightsStrip';
import { PageTemplate } from '../../components/layout/PageTemplate';
import { useConsumerAuth } from '../../contexts/ConsumerAuthContext';
import { useCases } from '../../hooks/useCases';
import { useDocuments } from '../../hooks/useDocuments';
import { useMySubmissions } from '../../lib/trpc/hooks';

export default function EndConsumerDashboard(): React.ReactElement {
  const { t } = useI18n();
  const { consumer } = useConsumerAuth();

  const { data: casesData, isLoading: casesLoading, error: casesError } = useCases(1, 50);
  const { data: submissionsData, isLoading: submissionsLoading } = useMySubmissions({ page: 1, limit: 50 });
  const { data: documentsData, isLoading: docsLoading } = useDocuments(1, 100);

  const firstName = useMemo(() => {
    const name = consumer?.name?.trim();
    if (!name) return t('consumer.home.greeting.defaultFirstName');
    return name.split(/\s+/)[0] ?? name;
  }, [consumer?.name, t]);

  const stats = useMemo(() => {
    const cases = (casesData?.cases ?? []) as { id: string; status: string }[];
    const submissions = (submissionsData?.submissions ?? []) as { id: string; status: string }[];
    const documents = (documentsData?.documents ?? []) as { id: string }[];

    const activeSubmissions = submissions.filter(
      (s) => !['converted', 'rejected'].includes((s.status ?? '').toLowerCase()),
    ).length;
    const activeCaseCount = cases.filter(
      (c) => !['closed', 'approved', 'rejected'].includes((c.status ?? '').toLowerCase()),
    ).length;
    const pendingActions =
      cases.filter((c) => ['pending', 'new'].includes((c.status ?? '').toLowerCase())).length +
      submissions.filter((s) => ['submitted', 'reviewing'].includes((s.status ?? '').toLowerCase())).length;

    return {
      activeCases: activeSubmissions + activeCaseCount,
      pendingActions,
      totalDocuments: documents.length,
    };
  }, [casesData, submissionsData, documentsData]);

  const isLoading = casesLoading || submissionsLoading || docsLoading;

  // Greeting stats — end-consumer doesn't have portfolio exposure, so we
  // surface application + pending counts instead of dollar amounts.
  const greetingStats = useMemo<GreetingHeaderStat[]>(() => {
    if (isLoading) return [];
    return [
      {
        id: 'active',
        tone: 'primary',
        label: t('consumer.home.greeting.statActiveApplications', { count: stats.activeCases }),
      },
      {
        id: 'pending',
        tone: 'warning',
        label: t('consumer.home.greeting.statPendingActions', { count: stats.pendingActions }),
      },
    ];
  }, [isLoading, stats, t]);

  return (
    <PageTemplate variant="dashboard" titleKey="consumer.home.greeting.title" icon={HomeIcon}>
      {casesError && (
        <GlassAlert severity="error" visible={true} sx={{ mb: 3 }}>
          {t('dashboard.failedToLoadDashboard')}
        </GlassAlert>
      )}

      <Stack spacing={4}>
        {/* 1. Greeting header — date eyebrow + hero greeting + inline stats + actions */}
        <GreetingHeader firstName={firstName} stats={greetingStats} />

        {/* 2. Astor briefing hero */}
        <AstorBriefing firstName={firstName} />

        {/* 3. KPI strip (3 tiles for end-consumer — no portfolio exposure) */}
        <Grid container spacing={1.5}>
          <Grid size={{ xs: 12, sm: 4 }}>
            <KpiTile
              tone="mint"
              icon={<AssignmentIcon sx={{ fontSize: iconSizes.base }} />}
              label={t('consumer.home.kpi.activeApplications')}
              value={isLoading ? '—' : String(stats.activeCases)}
              sub={t('consumer.home.kpi.activeApplicationsSub')}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <KpiTile
              tone="amber"
              icon={<PendingIcon sx={{ fontSize: iconSizes.base }} />}
              label={t('consumer.home.kpi.pendingActions')}
              value={isLoading ? '—' : String(stats.pendingActions)}
              sub={t('consumer.home.kpi.pendingActionsSub')}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <KpiTile
              tone="id"
              icon={<FolderIcon sx={{ fontSize: iconSizes.base }} />}
              label={t('consumer.home.kpi.documents')}
              value={isLoading ? '—' : String(stats.totalDocuments)}
              sub={t('consumer.home.kpi.documentsSub')}
            />
          </Grid>
        </Grid>

        {/* 4. Active applications panel (same component as borrower) */}
        <ActiveLoansPanel />

        {/* 5. Insights strip */}
        <PortfolioInsightsStrip />
      </Stack>
    </PageTemplate>
  );
}
